import { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";

import useAxios from "../../utils/useAxios";
import { resObj, paginationVal } from "../../utils/constants";
import PermissionContext from "../../context/PermissionContext";
import DrawerContext from "../../context/DrawerContext";
import AutoLogout from "../../utils/useAutoLogout";

import Pagination from '@mui/material/Pagination';

import SideDrawer from "../../components/layout/SideDrawer";
import Loader from "../../components/layout/Loader";
import ListHeader from "../../components/layout/ListHeader";
import CustomTable from "../../components/layout/CustomTable";
import SuccessAlert from "../../components/layout/SuccessAlert";
import NoData from "../../components/layout/NoData";
import ErrorModal from "../../components/layout/ErrorModal";
import InputModalForm from "../../components/layout/InputModalForm";
import DeleteModal from "../../components/layout/DeleteModal";

const FileTypeListPage = () => {
  const api = useAxios();
  const navigate = useNavigate();

  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  useEffect(() => {
    if(permisionsList.length > 0) checkUserPermission(['Admin-Manage Organisation']);
  }, [permisionsList]);

  const tableHeading = ['SI No', 'File Type', 'Columns'];
  const deleteModalTitle = 'File Type Delete';
  const deleteModalMsg = 'Are you sure you want to delete (File Type will be lost)?';

  const pageData = useRef({...paginationVal});
  const [apiRes, setApiRes] = useState(resObj);
  const [fileTypeList, setFileTypeList] = useState([]);
  const [fileTypeId, setFileTypeId] = useState('');
  const [showFTModal, setShowFTModal] = useState(false);
  const [fileType, setFileType] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    getFileTypes();
  }, []);

  const getFileTypes = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/user/organisation/file-type/list/get', {
        params: { 
          search_term: pageData.current.searchTerm,
          page_number: +pageData.current.pageNumber,
        }
      });
      
      if (response.status === 200) {
        setApiRes({ ...apiRes, loading: false });
        setFileTypeList(response.data.data);
        pageData.current.pageCount = response.data.page_count;
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Logs Get',
        loading: false,
      });
    }
  };

  const navigateToEdit = id => {
    navigate(`/file-type/edit/${id}`);
  };

  const handlePaginationClick = (e, pageNumber) => {
    pageData.current.pageNumber = pageNumber;
    getFileTypes();
  }; 

  const fileTypeCreate = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.post('/api/user/organisation/file-type/create', {
        file_type: fileType
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        setShowFTModal(false);
        setFileType('');
        getFileTypes();
      }
    } catch (err) {
      const errMsg = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '');
      if (errMsg === 'File Type already exists.') {
        setFormErrors({ 
          fileType: 'File Type already exists.' 
        });
      }
      else {
        setShowFTModal(false);
        setFileType('');
        setApiRes({
          ...apiRes,
          axiosError: true,
          errMsg: errMsg,
          errHeading: 'File Type Create',
          loading: false,
        });
      }
    }
  };

  const validate = () => {
    const errors = {};
    if (!fileType) {
      errors.fileType = 'File Type is required!';
    }
    return errors;
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      fileTypeCreate();
    }
  }, [formErrors]);

  const handleSubmit = e => {
    e.preventDefault();
    setFormErrors(validate());
    setIsSubmit(true);
  };

  const fileTypeDelete = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.delete(`/api/user/organisation/file-type/delete/${fileTypeId}`);

      if (response.status === 200) {
        setDeleteModalOpen(false);
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'File Type deleted successfully',
          loading: false,
        });
      }
      getFileTypes();
    } catch (err) {
      setDeleteModalOpen(false);
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'File Type Delete',
        loading: false,
      });
    }
  };

  return(
    <AutoLogout>
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      {apiRes.loading && <Loader />}
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <ListHeader 
          title="File Types" 
          handleClick={() => setShowFTModal(true)} 
          search={getFileTypes}
          pageData={pageData}
          hasCreatePermission={permisionsList.includes('Admin-Manage Organisation')}
        />

        <CustomTable 
          heading={tableHeading} 
          rows={fileTypeList} 
          navigateToEdit={navigateToEdit}
          setDeleteModalOpen={setDeleteModalOpen}
          setId={setFileTypeId}
          pageData={pageData}
          idKey={'id'}
          isDeleteBtnVisible={permisionsList.includes('Admin-Manage Organisation')}
          showToolTip={false}
        />

        {fileTypeList.length === 0 && <NoData />}

        {
          (fileTypeList.length !== 0 && pageData.current.pageCount !== 1) 
            && 
          <Pagination 
            style={{marginTop: '10px', float: 'right'}} 
            count={pageData.current.pageCount} 
            onChange={handlePaginationClick}
          />
        }

        <InputModalForm 
          title={'File Type Create'} 
          label={'File Type'} 
          show={showFTModal} 
          setShow={setShowFTModal} 
          value={fileType} 
          setValue={setFileType} 
          formErrors={formErrors} 
          errorKey={'fileType'} 
          handleSubmit={handleSubmit} 
        />

        <DeleteModal
          title={deleteModalTitle} 
          message={deleteModalMsg} 
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDelete={fileTypeDelete}
        />

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />

      </div>
    </AutoLogout>
  );
};

export default FileTypeListPage;